import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany, PageHeader } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, AlertTriangle } from "lucide-react";

const SUGGESTIONS = [
  "Combien ai-je vendu ce mois ?",
  "Quels clients dois-je relancer ?",
  "Quels produits seront bientôt en rupture ?",
  "Quelle est ma trésorerie disponible ?",
  "Quelle trésorerie dans 30 jours ?",
];

const ACTION_ROUTE = { relances: "/relances", produits: "/produits", depenses: "/depenses", rapports: "/rapports" };

export default function Copilot() {
  const { companyId } = useCompany();
  const navigate = useNavigate();
  const [attention, setAttention] = useState(null);
  const [msgs, setMsgs] = useState([{ role: "ai", text: "Bonjour 👋 Je suis NATORIQ Intelligence, votre copilote de gestion. Posez-moi une question sur vos ventes, votre trésorerie, vos créances, vos stocks ou vos dépenses." }]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  useEffect(() => {
    if (!companyId) return;
    api.get("/intelligence/attention", { params: { company_id: companyId } })
      .then(({ data }) => setAttention(data)).catch(() => {});
  }, [companyId]);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text) return;
    setMsgs((m) => [...m, { role: "user", text }]); setQ(""); setLoading(true);
    try {
      const { data } = await api.post("/copilot", { question: text }, { params: { company_id: companyId } });
      setMsgs((m) => [...m, { role: "ai", text: data.answer }]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setLoading(false); }
  };

  return (
    <div data-testid="copilot-page" className="max-w-3xl space-y-5">
      <PageHeader title="NATORIQ Intelligence" subtitle="Votre copilote de gestion alimenté par les données de votre entreprise." />

      {attention && attention.items?.length > 0 && (
        <Card className="p-5 rounded-md border-primary/30 bg-accent/40 shadow-sm" data-testid="attention-panel">
          <div className="flex items-center gap-2 font-display font-semibold mb-3">
            <Sparkles className="w-5 h-5 text-primary" />
            {attention.count} chose(s) nécessitent votre attention aujourd'hui
          </div>
          <div className="space-y-2.5">
            {attention.items.map((it, i) => (
              <div key={i} className="flex items-center justify-between gap-3 bg-white rounded-md border border-border px-3 py-2.5" data-testid={`attention-${it.type}`}>
                <span className="flex items-center gap-2 text-sm">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${it.severity === "red" ? "bg-rose-500" : it.severity === "orange" ? "bg-amber-500" : "bg-emerald-500"}`} />
                  {it.title}
                </span>
                <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => navigate(ACTION_ROUTE[it.action] || "/dashboard")} data-testid={`attention-action-${it.type}`}>
                  {it.action_label}
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="rounded-md border-border shadow-sm flex flex-col h-[60vh]">
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}-${i}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-primary text-white" : "bg-secondary text-foreground"}`}>
                {m.role === "ai" && <Sparkles className="w-4 h-4 text-primary inline mr-1 -mt-0.5" />}{m.text}
              </div>
            </div>))}
          {loading && <div className="flex justify-start"><div className="bg-secondary rounded-2xl px-4 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div></div>}
          <div ref={endRef} />
        </div>
        <div className="border-t border-border p-3">
          <div className="flex gap-1.5 flex-wrap mb-2">
            {SUGGESTIONS.map((s) => <button key={s} onClick={() => ask(s)} className="text-xs px-3 py-1 rounded-full bg-accent text-primary hover:bg-primary hover:text-white transition-colors" data-testid={`suggestion-${s.slice(0, 6)}`}>{s}</button>)}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Posez votre question…" data-testid="copilot-input" />
            <Button type="submit" disabled={loading} className="rounded-full" data-testid="copilot-send"><Send className="w-4 h-4" /></Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
